// Contenido Teórico Especializado - Pensamiento Matemático EXANI-II (Ceneval 2026)
// Elaborado con estándar pedagógico docente especialista en reactivos de admisión.

export const PENSAMIENTO_MATEMATICO_THEORY = {
  pm_101: {
    title: "Jerarquía de Operaciones, Fracciones y Decimales",
    intro: "El EXANI-II evalúa tu capacidad para operar números reales bajo la estricta jerarquía de operaciones y simplificar fracciones complejas. Es la base de al menos 4 de los 30 reactivos matemáticos.",
    sections: [
      {
        subtitle: "1. Regla de Oro: Jerarquía de Operaciones (PEMDAS / PAPOMUDAS)",
        content: "En matemáticas y en las evaluaciones de Ceneval, las operaciones no se resuelven de izquierda a derecha sin criterio; se rigen por un orden jerárquico inalterable:\n1. Paréntesis, corchetes y llaves [ ], { }, ( ): Se resuelven desde el más interno hacia el más externo.\n2. Exponentes y Radicación: Potencias y raíces.\n3. Multiplicación y División: Tienen exactamente el mismo nivel jerárquico; si aparecen juntas, se resuelven estrictamente de izquierda a derecha.\n4. Adición y Sustracción (Sumas y Restas): Último nivel jerárquico; de izquierda a derecha."
      },
      {
        subtitle: "2. Operaciones con Fracciones (Números Racionales)",
        content: "El sustentante de EXANI-II debe dominar tres conversiones automáticas:\n- Suma y Resta: Si tienen igual denominador, se suman/restan numeradores. Si tienen diferente denominador, se halla el mcm o método cruzado: (a/b) ± (c/d) = (ad ± bc) / bd\n- Multiplicación: Numerador por numerador y denominador por denominador: (a/b) * (c/d) = (a*c) / (b*d)\n- División: Multiplicación en cruz o Ley del Sándwich: (a/b) ÷ (c/d) = (ad) / (bc)\n- Fracciones Mixtas a Impropias: A b/c = ((A * c) + b) / c. Ejemplo: 3 1/2 = ((3 * 2) + 1) / 2 = 7/2."
      },
      {
        subtitle: "3. Conversión Fracción-Decimal para Ahorro de Tiempo",
        content: "Memorizar estas equivalencias te ahorrará hasta 45 segundos por reactivo:\n- 1/2 = 0.5\n- 1/4 = 0.25 | 3/4 = 0.75\n- 1/5 = 0.2 | 2/5 = 0.4 | 3/5 = 0.6 | 4/5 = 0.8\n- 1/8 = 0.125 | 3/8 = 0.375 | 5/8 = 0.625\n- 1/3 ≈ 0.333... | 2/3 ≈ 0.666..."
      }
    ],
    examples: [
      {
        problem: "Resuelve la expresión: 24 - 4 * (3 + 2)^2 ÷ 10 + 6",
        solution: "Paso 1 (Paréntesis): (3 + 2) = 5. Queda: 24 - 4 * 5^2 ÷ 10 + 6.\nPaso 2 (Exponente): 5^2 = 25. Queda: 24 - 4 * 25 ÷ 10 + 6.\nPaso 3 (Multiplicación y División de izquierda a derecha):\nPrimero: 4 * 25 = 100.\nLuego: 100 ÷ 10 = 10. Queda: 24 - 10 + 6.\nPaso 4 (Sumas y restas de izquierda a derecha):\n24 - 10 = 14.\n14 + 6 = 20.\nResultado: 20."
      }
    ],
    exam_tips: "Cuidado con la calculadora básica permitida por Ceneval: la mayoría no respeta la jerarquía de operaciones. Si tecleas '24 - 4 x 5', calculará '(24 - 4) x 5'. Debes ingresar las multiplicaciones y divisiones por separado."
  },

  pm_102: {
    title: "Razones, Proporciones y Regla de Tres Directa e Inversa",
    intro: "Las proporciones son el corazón del razonamiento cuantitativo en Ceneval. Te permiten modelar mezclas, tiempos de trabajo conjunto, rendimientos y escalas geométricas.",
    sections: [
      {
        subtitle: "1. Razón y Proporción Geométrica",
        content: "Una razón es el cociente entre dos cantidades: r = a / b.\nUna proporción es la igualdad entre dos razones: a/b = c/d <==> a * d = b * c"
      },
      {
        subtitle: "2. Regla de Tres Directa vs Inversa (El Criterio Clave)",
        content: "Para no equivocarte, hazte siempre esta pregunta:\n- Proporción Directa: Si una magnitud aumenta, la otra aumenta en la misma proporción (ej. más kg de producto -> mayor costo; más gasolina -> mayor distancia recorrida).\nFórmula: x = (b * c) / a\n- Proporción Inversa: Si una magnitud aumenta, la otra disminuye proporcionalmente (ej. más obreros -> menos días de obra; mayor velocidad -> menor tiempo; más llaves llenando -> menos horas).\nFórmula: x = (a * b) / c"
      },
      {
        subtitle: "3. Reparto Proporcional",
        content: "Cuando una cantidad total T se reparte de forma proporcional a k1, k2, k3:\n1. Constante de proporcionalidad: K = T / (k1 + k2 + k3)\n2. A cada beneficiario le toca: Pi = K * ki."
      }
    ],
    examples: [
      {
        problem: "3 bombas de agua idénticas vacían una cisterna en 8 horas. ¿Cuántas horas tardarán 4 bombas con el mismo caudal?",
        solution: "Identificamos el tipo de proporción: a MÁS bombas, se tardarán MENOS tiempo -> Proporción Inversa.\nPlanteamiento:\n3 bombas -> 8 horas\n4 bombas -> x horas\nCálculo inverso: x = (3 * 8) / 4 = 24 / 4 = 6 horas."
      }
    ],
    exam_tips: "El distractor más común en Ceneval para este problema es aplicar regla de tres directa: (4 * 8) / 3 = 10.66 horas. Si más bombas tardan más tiempo, es absurdo. Comprueba siempre la lógica del resultado."
  },

  pm_103: {
    title: "Porcentajes, Descuentos Sucesivos e Interés Simple",
    intro: "Aparece con certeza absoluta en el examen. Incluye cálculo de IVA, descuentos en tiendas comerciales, ganancias y la fórmula bancaria de interés simple.",
    sections: [
      {
        subtitle: "1. El Concepto de Porcentaje como Factor Decimal",
        content: "El P% de una cantidad C es: Valor = C * (P / 100)\n- Aumentar el 16% equivale a multiplicar por 1.16.\n- Disminuir el 25% equivale a pagar el 75%, multiplicando por 0.75."
      },
      {
        subtitle: "2. Descuentos Sucesivos (¡Trampa Clásica Ceneval!)",
        content: "Un artículo con 20% de descuento y un 10% adicional NO tiene un 30% de descuento.\nSe calculan sobre el remanente:\nFactor final = 0.80 * 0.90 = 0.72.\nPor lo tanto, se paga el 72% y el descuento real es de solo el 28%."
      },
      {
        subtitle: "3. Interés Simple Financiero",
        content: "Fórmula obligatoria: I = C * r * t\n- I: Interés generado ($)\n- C: Capital inicial depositado o prestado ($)\n- r: Tasa de interés anual expresada en decimal (ej. 5% = 0.05)\n- t: Tiempo en años.\nMonto final acumulado: M = C + I = C * (1 + r * t)"
      }
    ],
    examples: [
      {
        problem: "Un estudiante deposita $12,000 MXN a una tasa de interés simple anual del 5% durante 3 años. ¿Cuál es el monto total al finalizar el plazo?",
        solution: "Datos: C = 12000, r = 0.05, t = 3 años.\nInterés: I = 12000 * 0.05 * 3 = 12000 * 0.15 = $1,800 MXN.\nMonto total: M = 12000 + 1800 = $13,800 MXN."
      }
    ],
    exam_tips: "Verifica siempre la unidad de tiempo: si la tasa es anual y el tiempo te lo dan en meses, divide los meses entre 12 (ej. 6 meses = 6/12 = 0.5 años)."
  },

  pm_104: {
    title: "Lenguaje Algebraico y Operaciones con Polinomios",
    intro: "El EXANI-II evalúa la traducción precisa del lenguaje natural a expresiones algebraicas y la suma, resta, producto y división de polinomios.",
    sections: [
      {
        subtitle: "1. Diccionario de Traducción Algebraica",
        content: "- 'Un número cualquiera': x\n- 'El doble de un número aumentado en 5': 2x + 5\n- 'El cuadrado de la suma de dos números': (x + y)^2\n- 'La suma de los cuadrados de dos números': x^2 + y^2\n- 'El producto de dos números consecutivos': x(x + 1)\n- 'La semi-diferencia de dos números': (x - y) / 2"
      },
      {
        subtitle: "2. Leyes de Exponentes Fundamentales",
        content: "1. Producto de igual base: x^a * x^b = x^(a+b)\n2. Cociente de igual base: x^a / x^b = x^(a-b)\n3. Potencia de potencia: (x^a)^b = x^(a*b)\n4. Exponente negativo: x^(-n) = 1 / x^n\n5. Exponente fraccionario: x^(m/n) = raíz-n de x^m\n6. Exponente cero: x^0 = 1"
      }
    ],
    examples: [
      {
        problem: "Simplifica: (2x^3 y^2)^3 / (4x^5 y^4)",
        solution: "Desarrollamos numerador: 2^3 * (x^3)^3 * (y^2)^3 = 8 x^9 y^6.\nDividimos entre 4x^5 y^4:\nCoeficiente: 8 / 4 = 2.\nVariable x: x^(9-5) = x^4.\nVariable y: y^(6-4) = y^2.\nResultado: 2x^4 y^2."
      }
    ],
    exam_tips: "Cuidado con los signos en las restas de polinomios: (3x^2 - 5x) - (x^2 - 7x) = 3x^2 - 5x - x^2 + 7x = 2x^2 + 2x."
  },

  pm_105: {
    title: "Productos Notables y Métodos de Factorización",
    intro: "Factorizar es el superpoder del aspirante. Te permite resolver ecuaciones cuadráticas y simplificar fracciones algebraicas en segundos sin necesidad de la fórmula general.",
    sections: [
      {
        subtitle: "1. Productos Notables Clave",
        content: "- Binomio al cuadrado: (a ± b)^2 = a^2 ± 2ab + b^2 (Trinomio Cuadrado Perfecto)\n- Binomios conjugados: (a + b)(a - b) = a^2 - b^2 (Diferencia de Cuadrados)\n- Binomios con término común: (x + a)(x + b) = x^2 + (a + b)x + ab"
      },
      {
        subtitle: "2. Casos de Factorización más Frecuentes en Ceneval",
        content: "1. Factor Común: Extraer el MCD numérico y la letra con menor exponente: 6x^3 + 9x^2 = 3x^2(2x + 3).\n2. Diferencia de Cuadrados: Dos términos restándose con raíz exacta: x^2 - 49 = (x - 7)(x + 7).\n3. Trinomio x^2 + bx + c: Buscar dos números que multiplicados den c y sumados den b:\nx^2 - 5x + 6 = (x - 3)(x - 2) (porque -3 * -2 = +6 y -3 - 2 = -5)."
      }
    ],
    examples: [
      {
        problem: "Simplifica la expresión: (x^2 - 16) / (x^2 + 7x + 12)",
        solution: "Numerador: (x - 4)(x + 4).\nDenominador: dos números que multiplicados den 12 y sumados 7 -> 3 y 4: (x + 3)(x + 4).\nCancelamos factor común (x + 4):\nResultado: (x - 4) / (x + 3)."
      }
    ],
    exam_tips: "En simplificación de fracciones algebraicas, si se te dificulta factorizar, asigna un valor numérico simple a x (ej. x = 2) y evalúa la expresión original frente a las 3 opciones."
  },

  pm_106: {
    title: "Ecuaciones Lineales y Sistemas de Ecuaciones 2x2",
    intro: "Mide tu capacidad de modelado mediante problemas verbales (boletos de cine, edades, mezclas y dimensiones geométricas).",
    sections: [
      {
        subtitle: "1. Ecuaciones Lineales de Primer Grado",
        content: "Una ecuación lineal representa una recta. Su forma general es ax + b = 0.\nObjetivo: Aislar la incógnita x aplicando operaciones inversas a ambos lados de la igualdad."
      },
      {
        subtitle: "2. Sistemas de Ecuaciones 2x2",
        content: "Conjunto de dos ecuaciones con dos incógnitas.\nMétodos estándar:\n- Reducción (Suma y Resta): Multiplicar para cancelar una variable.\n- Sustitución: Despejar en una y sustituir en la otra.\n- Determinantes (Cramer): Para obtener directamente x o y."
      }
    ],
    examples: [
      {
        problem: "En un estacionamiento hay 35 vehículos entre autos y motocicletas. Si en total se cuentan 100 ruedas, ¿cuántos autos hay?",
        solution: "Definimos: a = autos (4 ruedas), m = motos (2 ruedas).\n1) a + m = 35\n2) 4a + 2m = 100\nMultiplicamos la ecuación 1 por -2: -2a - 2m = -70.\nSumamos con ec 2: 2a = 30 ==> a = 15.\nHay 15 autos."
      }
    ],
    exam_tips: "¡Estrategia Ceneval! Con 3 opciones de respuesta, muchas veces es más rápido sustituir las opciones en las condiciones del problema para ver cuál cumple ambas."
  },

  pm_107: {
    title: "Ecuaciones Cuadráticas y Representación de la Parábola",
    intro: "Las ecuaciones de segundo grado (ax^2 + bx + c = 0) modelan trayectorias parabólicas, áreas máximas e ingresos óptimos.",
    sections: [
      {
        subtitle: "1. Métodos de Solución",
        content: "1. Fórmula General: x = (-b ± √(b^2 - 4ac)) / (2a)\n2. Discriminante Δ = b^2 - 4ac:\n- Δ > 0: Dos soluciones reales y distintas (corta al eje X en dos puntos).\n- Δ = 0: Una solución real única (la parábola es tangente al eje X).\n- Δ < 0: No existen soluciones reales (la parábola no corta al eje X)."
      },
      {
        subtitle: "2. Vértice de la Parábola",
        content: "Para y = ax^2 + bx + c:\nCoordenada x del vértice: xv = -b / (2a)\nSi a > 0: abre hacia arriba (vértice es mínimo).\nSi a < 0: abre hacia abajo (vértice es máximo)."
      }
    ],
    examples: [
      {
        problem: "¿Cuáles son las raíces de x^2 - 7x + 10 = 0?",
        solution: "Factorizamos buscando dos números que multiplicados den 10 y sumados -7 -> -5 y -2.\n(x - 5)(x - 2) = 0.\nx1 = 5, x2 = 2."
      }
    ],
    exam_tips: "Recuerda que la suma de raíces es -b/a y el producto es c/a. Esto te permite verificar la opción correcta en 5 segundos."
  },

  pm_108: {
    title: "Geometría Euclidiana: Perímetros, Áreas y Teorema de Pitágoras",
    intro: "Evalúa figuras compuestas, cálculo de áreas sombreadas y relaciones métricas en triángulos rectángulos.",
    sections: [
      {
        subtitle: "1. Fórmulas de Áreas Indispensables",
        content: "- Triángulo: A = (b * h) / 2\n- Cuadrado: A = l^2 | Rectángulo: A = b * h\n- Círculo: A = π * r^2 | Circunferencia: P = 2 * π * r\n- Trapecio: A = ((B + b) * h) / 2\n- Rombo: A = (D * d) / 2"
      },
      {
        subtitle: "2. Teorema de Pitágoras y Ternas Pitagóricas",
        content: "En todo triángulo rectángulo: c^2 = a^2 + b^2 ==> c = √(a^2 + b^2)\nTernas Pitagóricas más usadas en Ceneval:\n- 3, 4, 5 (y múltiplos: 6-8-10, 9-12-15)\n- 5, 12, 13\n- 8, 15, 17"
      }
    ],
    examples: [
      {
        problem: "Una escalera de 10 m se apoya contra una pared. Si la base está a 6 m de la pared, ¿a qué altura llega?",
        solution: "Hipotenusa c = 10, cateto a = 6.\nAltura h = √(10^2 - 6^2) = √(100 - 36) = √64 = 8 metros."
      }
    ],
    exam_tips: "En áreas sombreadas, la fórmula maestra es: Área Sombreada = Área Figura Externa - Área Figura Interna."
  },

  pm_109: {
    title: "Trigonometría Básica (Razones Trigonométricas y Leyes)",
    intro: "Comprende el uso de seno, coseno y tangente en triángulos rectángulos y el cálculo de ángulos de elevación y depresión.",
    sections: [
      {
        subtitle: "1. Las Razones Trigonométricas (SOH-CAH-TOA)",
        content: "- sen(θ) = Cateto Opuesto / Hipotenusa\n- cos(θ) = Cateto Adyacente / Hipotenusa\n- tan(θ) = Cateto Opuesto / Cateto Adyacente"
      },
      {
        subtitle: "2. Ángulo de Elevación vs Depresión",
        content: "- Elevación: Se mide desde la horizontal hacia ARRIBA.\n- Depresión: Se mide desde la horizontal hacia ABAJO."
      }
    ],
    examples: [
      {
        problem: "Desde un faro de 30 m de altura, el ángulo de elevación hacia un avión es de 45°. Si la distancia horizontal al avión es de 50 m, ¿a qué altura vuela?",
        solution: "tan(45°) = 1.\ntan(45°) = Cateto Opuesto / 50 = 1 ==> Cateto Opuesto = 50 m sobre el faro.\nAltura total = 30 + 50 = 80 metros."
      }
    ],
    exam_tips: "Valores notables a memorizar: sen(30°) = 0.5, cos(60°) = 0.5, tan(45°) = 1."
  },

  pm_110: {
    title: "Estadística Descriptiva y Probabilidad Clásica",
    intro: "Mide tu dominio sobre media, mediana, moda, rango y probabilidad de eventos simples y compuestos.",
    sections: [
      {
        subtitle: "1. Medidas de Tendencia Central",
        content: "- Media aritmética (Promedio): suma de datos / N\n- Mediana: Valor central tras ordenar de menor a mayor. Si N es par, promedio de los dos centrales.\n- Moda: El valor que más se repite."
      },
      {
        subtitle: "2. Regla de Laplace para Probabilidad Clásica",
        content: "P(A) = Casos Favorables / Casos Posibles Totales\nLa probabilidad siempre está entre 0 (imposible) y 1 (seguro).\nEventos independientes: P(A y B) = P(A) * P(B)."
      }
    ],
    examples: [
      {
        problem: "En una urna hay 5 bolas rojas, 3 azules y 2 verdes. ¿Probabilidad de que al extraer una NO sea roja?",
        solution: "Total = 5 + 3 + 2 = 10.\nCasos favorables (no rojas) = 3 azules + 2 verdes = 5.\nP(No roja) = 5 / 10 = 1/2 = 0.5 = 50%."
      }
    ],
    exam_tips: "Para la mediana, el error más común es olvidar ordenar los datos de menor a mayor antes de ubicar el centro."
  }
};

